import api from 'core/util/api';

// The Authorization Server refused to send the code to the client's redirect host.
export class OAuthRedirectRefused extends Error {
  constructor(readonly redirectHost: string) {
    super('Authorization redirect is not allowed');
  }
}

function redirectRefusal(failure: unknown): OAuthRedirectRefused | undefined {
  if (!failure || typeof failure !== 'object' || !('status' in failure) || failure.status !== 403) {
    return undefined;
  }
  const body = 'body' in failure ? failure.body : undefined;
  if (
    !body ||
    typeof body !== 'object' ||
    !('error' in body) ||
    body.error !== 'redirect_not_allowed' ||
    !('redirectHost' in body) ||
    typeof body.redirectHost !== 'string'
  ) {
    return undefined;
  }
  return new OAuthRedirectRefused(body.redirectHost);
}

// The attempt reference from an /oauth/authorize query, which must carry exactly one transaction and nothing else.
export function parseAuthorizationAttempt(search: string): string | undefined {
  const parameters = new URLSearchParams(search);
  const transactionId = parameters.get('transaction');
  if (
    !transactionId ||
    !/^[A-Za-z0-9_-]{1,128}$/.test(transactionId) ||
    parameters.getAll('transaction').length !== 1 ||
    [...parameters.keys()].some((key) => key !== 'transaction')
  ) {
    return undefined;
  }
  return transactionId;
}

export class OAuthAuthorizationService {
  private completions = new Map<string, Promise<string>>();

  complete(search: string): Promise<string> {
    const transactionId = parseAuthorizationAttempt(search);
    if (!transactionId) {
      return Promise.reject(new Error('Invalid authorization attempt'));
    }
    const existing = this.completions.get(transactionId);
    if (existing) {
      return existing;
    }
    const completion = Promise.resolve()
      .then(() =>
        api.post('/api/ui/oauth/complete', {
          data: { transactionId },
          showErrorToast: false,
          noRedirect: true,
          // Raw responses keep the refusal body on failure, which is otherwise reduced to its status text.
          rawResponse: true
        })
      )
      .catch((failure: unknown) => {
        throw redirectRefusal(failure) ?? failure;
      })
      .then((response: unknown) => {
        const result = response && typeof response === 'object' && 'body' in response ? response.body : undefined;
        if (
          !result ||
          typeof result !== 'object' ||
          !('redirectUrl' in result) ||
          typeof result.redirectUrl !== 'string'
        ) {
          throw new Error('Invalid authorization completion');
        }
        const target = new URL(result.redirectUrl);
        if (
          (target.protocol !== 'https:' &&
            !(target.protocol === 'http:' && ['localhost', '127.0.0.1', '[::1]'].includes(target.hostname))) ||
          target.username ||
          target.password ||
          target.hash ||
          target.pathname !== `/oauth/interaction/${transactionId}/complete` ||
          target.searchParams.getAll('handoff').length !== 1 ||
          !/^[A-Za-z0-9_-]{43}$/.test(target.searchParams.get('handoff') ?? '') ||
          [...target.searchParams.keys()].some((key) => key !== 'handoff')
        ) {
          throw new Error('Invalid authorization completion');
        }
        return target.href;
      });
    this.completions.set(transactionId, completion);
    return completion;
  }
}
